<script setup lang="ts">
/**
 * La fiche d'une surveillance (écran 2 du dossier), sous la fiche de son
 * équipement, en lecture.
 *
 * Ce que l'utilisateur doit vivre : il ouvre une surveillance depuis la liste
 * ou depuis la fiche d'une sonde, et lit ce qu'elle surveille, ses seuils, si
 * elle suit les horaires du site, et ce qui lui est arrivé. La page ne lit
 * jamais le détail d'une alarme (il clôt une alarme revenue à la normale et
 * non vue, D71) : elle y mène par un lien.
 *
 * Lot F6 : les gestes de la fiche écrivent, puis la page RELIT la
 * surveillance (`ficheStore.relire`), sans la vider pendant la relecture.
 */
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { useFicheStore } from "@modules/alarmes-v2/stores/fiche.store"
import { useTypesStore } from "@modules/alarmes-v2/stores/types.store"
import { useMembresStore } from "@modules/alarmes-v2/stores/membres.store"
import { CODE_INDISPONIBLE } from "@modules/alarmes-v2/services/appel"
import { SEGMENT_SURVEILLANCE } from "@modules/alarmes-v2/utils/menu"
import { cleDuRefus } from "@modules/alarmes-v2/utils/refus"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    objetType: "source" | "building"
    idObjet: string
    idInstance: string
}>()

const authStore = useAuthStore()
const ficheStore = useFicheStore()
const typesStore = useTypesStore()
const membresStore = useMembresStore()

/**
 * Le chemin de l'onglet de l'équipement.
 */
const getCheminOnglet = computed(
    (): string =>
        `/${props.objetType}/${encodeURIComponent(props.idObjet)}/${SEGMENT_SURVEILLANCE}`
)

/**
 * Le chemin de cette fiche.
 */
const getCheminFiche = computed(
    (): string =>
        `${getCheminOnglet.value}/${encodeURIComponent(props.idInstance)}`
)

/**
 * La phrase d'un échec, à la place de la fiche.
 */
const getPhraseErreur = computed((): string => {
    const erreur = ficheStore.erreur
    if (!erreur || erreur.code === CODE_INDISPONIBLE)
        return t("alarmesV2.fiche.indisponible")
    return t(cleDuRefus(erreur))
})

/**
 * Le nom du type d'alarme, ou `null`.
 */
const getNomType = computed((): string | null => {
    const s = ficheStore.surveillance
    return s ? typesStore.nomDuType(s.type_code, s.type_version) : null
})

/**
 * Les nombres réglables du type, pour nommer les seuils.
 */
const getNombres = computed(() => {
    const s = ficheStore.surveillance
    return s ? typesStore.nombresDuType(s.type_code, s.type_version) : []
})

/**
 * Le type porte-t-il une astreinte (geste 32), ou `null`.
 */
const getAstreinte = computed((): boolean | null => {
    const s = ficheStore.surveillance
    return s ? typesStore.astreinteDuType(s.type_code, s.type_version) : null
})

/**
 * Relit la surveillance après une écriture : la surveillance de l'adresse,
 * jamais une autre. Vrai si la relecture a abouti.
 */
function relire(): Promise<boolean> {
    return ficheStore.relire({
        objetType: props.objetType,
        idObjet: props.idObjet,
        idInstance: props.idInstance,
    })
}

/**
 * Lit la fiche, si l'entreprise a l'offre.
 */
function lire(): void {
    if (!authStore.hasOfferEnergyManager) return
    void ficheStore.charger(
        {
            objetType: props.objetType,
            idObjet: props.idObjet,
            idInstance: props.idInstance,
        },
        { historique: true }
    )
    void typesStore.charger()
    // Les noms et l'état des numéros (lot F4), relus à chaque visite : un numéro
    // corrigé ailleurs ne reste pas « à corriger ». Jamais la relecture des
    // tests : elle part au clic, destinataire par destinataire.
    void membresStore.charger({ relire: true })
}

watch(
    () => [
        props.objetType,
        props.idObjet,
        props.idInstance,
        authStore.companyId,
        authStore.hasOfferEnergyManager,
    ],
    lire,
    { immediate: true }
)

/**
 * Relit après un échec.
 */
function onRelire(): void {
    ficheStore.reinitialiser()
    lire()
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreModule>
        <div class="flex flex-col gap-4 p-4 md:p-6">
            <RouterLink
                :to="getCheminOnglet"
                class="text-sm text-primary-700 underline"
                data-test="vers-onglet"
                >{{ t("alarmesV2.fiche.versLaListe") }}</RouterLink
            >
            <section
                class="rounded-lg border border-surface-200 bg-surface-0 p-4"
            >
                <p
                    v-if="
                        ficheStore.statut === 'chargement' ||
                        ficheStore.statut === 'attente'
                    "
                    class="text-sm text-surface-600"
                >
                    {{ t("alarmesV2.fiche.chargement") }}
                </p>
                <p
                    v-else-if="ficheStore.statut === 'introuvable'"
                    class="text-sm text-surface-800"
                    data-test="introuvable"
                >
                    {{ t("alarmesV2.fiche.introuvable") }}
                </p>
                <div
                    v-else-if="ficheStore.statut === 'erreur'"
                    class="flex flex-wrap items-center gap-3"
                    data-test="erreur"
                >
                    <p class="text-sm text-surface-800">
                        {{ getPhraseErreur }}
                    </p>
                    <button
                        type="button"
                        class="text-sm font-medium text-primary-700 underline"
                        @click="onRelire"
                    >
                        {{ t("alarmesV2.refus.relire") }}
                    </button>
                </div>
                <FicheSurveillance
                    v-else-if="ficheStore.surveillance"
                    :surveillance="ficheStore.surveillance"
                    :alarme-ouverte="ficheStore.alarmeOuverte"
                    :historique="ficheStore.historique"
                    :statut-historique="ficheStore.statutHistorique"
                    :historique-incomplet="ficheStore.historiqueIncomplet"
                    :personnes="ficheStore.personnes"
                    :nom-type="getNomType"
                    :nombres="getNombres"
                    :membres="membresStore.parId"
                    :statut-membres="membresStore.statut"
                    :chemin-fiche="getCheminFiche"
                    :astreinte="getAstreinte"
                    :relire="relire"
                />
            </section>
        </div>
    </CadreModule>
</template>
